import { useState } from "react";
import type { SceneState } from "../../../../shared/api-contract";
import type { BindingCtx, DomainRead } from "../../../../state/editor/types";
import { useDomainState, useEditorStore, useMutate, useUi } from "../../../../state/editor/useEditor";
import { useViewer } from "../../../../state/ViewerProvider";
import { CompactSelect } from "../../inspector/rows";

/**
 * The three Scene selectors with no canonical SceneState field, so they can't
 * use sc()/scReturning() bindings. Each is a small custom-row component that
 * calls the RPC directly and remembers its selection in the persisted
 * `ui.lastApplied` map (sections unmount when collapsed, so menu-local state
 * would otherwise reset).
 *
 * `ui.lastApplied` is persisted to localStorage, but the viewer re-boots to its
 * baseProject defaults on every page load and nothing re-applies the remembered
 * ids to it. A persisted id is therefore only trustworthy as a fallback:
 *  · the environment now has a canonical field (v44 `SceneState.envPresetId`),
 *    so that select reads the live value and only falls back to `lastApplied`
 *    on a pre-v44 viewer;
 *  · the other two have no engine-readable counterpart, so they fall back to
 *    `sessionApplied` — module scope, not useState, because it must survive the
 *    section unmounting on collapse but must NOT survive a reload the way
 *    localStorage does.
 */
const sessionApplied: Record<string, string | undefined> = {};

type CtxProps = { ctx: BindingCtx; read: DomainRead };

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** Environment HDR preset picker → setEnvironment(id). */
export function EnvPresetSelect(_props: CtxProps) {
  const { client, catalogs } = useViewer();
  const store = useEditorStore();
  // Subscribed with the hook rather than declared via the row's `domains` — the
  // RowDef lives in sections/scene.ts; useDomainState re-renders this component
  // on its own when the slice changes.
  const scene = useDomainState("scene");
  const [ui, patchUi] = useUi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // v44: `envPresetId` is what the viewer ACTUALLY has loaded ("custom" when an
  // HDR came in by URL, in which case no preset option matches and the select
  // stays blank rather than naming an environment that isn't loaded).
  const liveId = scene.envPresetId;
  const value = liveId
    ? (catalogs.envPresets.some((p) => p.id === liveId) ? liveId : "")
    : ui.lastApplied.environment ?? catalogs.envPresets[0]?.id ?? "";

  return (
    <div className="flex w-full flex-col gap-1">
      <CompactSelect
        value={value}
        options={catalogs.envPresets}
        onChange={(id) => {
          // Still recorded: the Environment left panel reads this same key.
          patchUi({ lastApplied: { ...ui.lastApplied, environment: id } });
          setBusy(true);
          setError(null);
          client.ring
            .setEnvironment(id)
            .catch((e: unknown) => setError(errMsg(e)))
            .finally(() => {
              setBusy(false);
              // Pull the real env id (plus whatever the load changed) back into
              // the slice — on success it confirms the switch, on failure it
              // snaps the select back to the environment still loaded.
              void store.refresh("scene");
            });
        }}
      />
      {/* An HDR is 1.4–1.6 MB and gets PMREM-prefiltered on arrival; without
          this the select looked instantly done while the scene was unchanged. */}
      {busy ? <p className="text-[11px] text-ed-text-3">Loading environment…</p> : null}
      {error ? <p className="text-[11px] text-ed-red">{error}</p> : null}
    </div>
  );
}

/** Tone-mapping operator picker → setToneMapping(number). */
const TONE_OPTIONS = [
  { id: "4", label: "ACES Filmic" },
  { id: "2", label: "Reinhard" },
  { id: "3", label: "Cineon" },
  { id: "1", label: "Linear" },
  { id: "0", label: "None" },
];

export function ToneMappingSelect(_props: CtxProps) {
  const { client } = useViewer();
  const [applied, setApplied] = useState(sessionApplied.toneMapping);
  const [error, setError] = useState<string | null>(null);
  // "4" (ACES) is the viewer's boot operator, so it is the honest label for a
  // session in which nothing has been picked yet — the persisted value was not.
  const value = applied ?? "4";

  return (
    <div className="flex w-full flex-col gap-1">
      <CompactSelect
        value={value}
        options={TONE_OPTIONS}
        onChange={(v) => {
          sessionApplied.toneMapping = v;
          setApplied(v);
          setError(null);
          client.ring.setToneMapping(Number(v)).catch((e: unknown) => {
            setError(errMsg(e));
            // The operator never changed — don't leave the select claiming it did.
            sessionApplied.toneMapping = applied;
            setApplied(applied);
          });
        }}
      />
      {error ? <p className="text-[11px] text-ed-red">{error}</p> : null}
    </div>
  );
}

/**
 * Studio lighting preset picker. applyLightPreset returns the full SceneState,
 * so we reconcile the whole scene slice through the store (replacing it with
 * the RPC result). Shows the selected preset's description beneath the select.
 */
export function StudioPresetSelect(_props: CtxProps) {
  const { catalogs } = useViewer();
  const mutate = useMutate();
  const [applied, setApplied] = useState(sessionApplied.lightPreset);
  const [error, setError] = useState<string | null>(null);
  // "defaultStudio" is the factory key/fill rig with no extra lights — what a
  // freshly booted viewer is running. Showing the persisted id here instead
  // labelled the rig with a preset that had not been applied since the reload.
  const presetId = applied ?? "defaultStudio";
  const preset = catalogs.lightPresets.find((p) => p.id === presetId);

  return (
    <div className="flex w-full flex-col gap-1.5">
      <CompactSelect
        value={presetId}
        options={catalogs.lightPresets}
        onChange={(id) => {
          const prev = applied;
          sessionApplied.lightPreset = id;
          setApplied(id);
          setError(null);
          mutate("scene", {
            call: (api) =>
              Promise.resolve(api.applyLightPreset(id)).catch((e: unknown) => {
                setError(errMsg(e));
                sessionApplied.lightPreset = prev;
                setApplied(prev);
                throw e; // let the store refresh the untouched rig back
              }),
            reconcile: (res) => res as SceneState,
          });
        }}
      />
      {preset ? (
        <p className="text-[12px] leading-snug text-ed-text-3">{preset.description}</p>
      ) : null}
      {error ? <p className="text-[12px] leading-snug text-ed-red">{error}</p> : null}
    </div>
  );
}
